import { AfterViewInit, ChangeDetectorRef, Component, computed, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { NgbActiveOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@services/auth.service';
import { DataViewConfigService } from '@services/dataview-config.service';
import { BaseComponent } from '@shared/components/base-component';
import { ChitinFormInputComponent } from '@shared/components/chitin/atoms/form-input/form-input.component';
import { Column, Filter } from '@shared/components/data-view/data-view-types';
import { sum } from 'lodash';
import { catchError, forkJoin } from 'rxjs';

@Component({
  selector: 'app-data-view-filter-gallery',
  templateUrl: './data-view-filter-gallery.component.html',
  styleUrls: ['./data-view-filter-gallery.component.scss'],
  standalone: false,
})
export class DataViewFilterGalleryComponent extends BaseComponent implements OnInit, AfterViewInit {
  @Input() pageName?: string;
  @Input() header?: string;
  appliedFilters: Filter.AppliedDataViewFilter[] = [];
  newlyAppliedFilters: Filter.AppliedDataViewFilter[] = [];
  newlyRemovedFilterIds: string[] = [];
  filterCategories: Column.DataViewColumnGalleryCategory[];
  @ViewChild('filterSearch') filterSearch: ChitinFormInputComponent<string>;

  private readonly cdr = inject(ChangeDetectorRef);

  isLoadingFilterDefinitions = signal(false);
  searchQuery = signal<string | null>(null);
  filters = signal<Record<string, Filter.DataViewFilter[]>>({});
  uncategorizedFilters = signal<Filter.DataViewFilter[]>([]);
  filtersFilteredBySearch = computed(() => this.getFilteredFilterCategories(this.searchQuery(), this.filters()));
  uncategorizedFiltersFilteredBySearch = computed(() => this.getFilteredFilters(this.searchQuery(), this.uncategorizedFilters()));
  filtersCount = computed(() => sum(Object.values(this.filters()).map(filters => filters.length)) + this.uncategorizedFilters().length);
  filteredFiltersCount = computed(
    () =>
      sum(Object.values(this.filtersFilteredBySearch()).map(filters => filters.length)) +
      this.uncategorizedFiltersFilteredBySearch().length,
  );

  constructor(
    protected activeOffcanvas: NgbActiveOffcanvas,
    private dataViewConfigService: DataViewConfigService,
    private authService: AuthService,
  ) {
    super();
  }

  ngOnInit() {
    if (this.pageName?.length) {
      this.setupFilters(this.pageName);
    }
  }

  ngAfterViewInit() {
    this.filterSearch?.focus();
  }

  private setupFilters(pageName: string) {
    this.isLoadingFilterDefinitions.set(true);
    this.subscribe(
      forkJoin({
        filterCategories: this.dataViewConfigService.getColumnCategories(pageName),
        filters: this.dataViewConfigService.getAvailableFilters(pageName, this.authService.getCurrentUser()),
      }).pipe(
        catchError(error => {
          console.error('Failed to load filter definitions:', error);
          this.isLoadingFilterDefinitions.set(false);
          return [];
        }),
      ),
      result => {
        this.setFilters(result.filters, result.filterCategories);
        this.isLoadingFilterDefinitions.set(false);
      },
    );
  }

  setFilters(filters: Filter.DataViewFilter[], filterCategories: Column.DataViewColumnGalleryCategory[]) {
    for (const filter of filters) {
      if (filter.required) continue;
      if (this.appliedFilters.filter(appliedFilter => appliedFilter.field === filter.field).length > 0) {
        filter.disabled = true;
      } else {
        filter.disabled = false;
      }
      if (filter.categoryId === '') {
        this.uncategorizedFilters().push(filter);
      } else {
        if (!(filter.categoryId in this.filters())) {
          this.filters()[filter.categoryId] = [];
        }
        this.filters()[filter.categoryId].push(filter);
      }
    }
    // Trigger signal change
    this.filters.set(this.filters());
    this.uncategorizedFilters.set([...this.uncategorizedFilters()]);

    this.filterCategories = filterCategories;
    this.cdr.markForCheck();
  }

  onFilterSelected(filter: Filter.DataViewFilter) {
    const alreadyAppliedIndex = this.appliedFilters.findIndex(appliedFilter => appliedFilter.id === filter.id);
    const newlyAppliedIndex = this.newlyAppliedFilters.findIndex(newlyAppliedFilter => newlyAppliedFilter.id === filter.id);
    const newlyRemovedIndex = this.newlyRemovedFilterIds.findIndex(newlyRemovedFilterId => newlyRemovedFilterId === filter.id);

    if (alreadyAppliedIndex !== -1) {
      // Filter has already been added
      if (filter.required) return;
      if (newlyRemovedIndex !== -1) {
        this.newlyRemovedFilterIds.splice(newlyRemovedIndex, 1);
        filter.disabled = true;
      } else {
        this.newlyRemovedFilterIds.push(filter.id);
        filter.disabled = false;
      }
    } else {
      // Filter has not yet been added
      if (newlyAppliedIndex !== -1) {
        this.newlyAppliedFilters.splice(newlyAppliedIndex, 1);
        filter.disabled = false;
      } else {
        this.newlyAppliedFilters.push({
          ...filter,
          type: filter.allowedTypes[0],
          value: undefined,
        });
        filter.disabled = true;
      }
    }
  }

  getActiveFilters() {
    const appliedFilters = this.appliedFilters.filter(appliedFilter => !this.newlyRemovedFilterIds.includes(appliedFilter.id));
    return [...appliedFilters, ...this.newlyAppliedFilters];
  }

  private getFilteredFilters(searchQuery: string | null, filters: Filter.DataViewFilter[]) {
    return filters.filter(f => !searchQuery || f.display.toLowerCase().includes(searchQuery.toLowerCase()));
  }

  private getFilteredFilterCategories(searchQuery: string | null, filters: Record<string, Filter.DataViewFilter[]>) {
    const filteredFilters: Record<string, Filter.DataViewFilter[]> = {};
    for (const fkey of Object.keys(filters)) {
      filteredFilters[fkey] = this.getFilteredFilters(searchQuery, filters[fkey]);
    }
    return filteredFilters;
  }
}
